import React from "react";
import api, { postForm } from "../api/client";
import { useOrg } from "../context/OrgContext";
import { useAuth } from "../context/AuthContext";
import { useTerminology } from "../context/TerminologyContext";
import { DateRangePicker, ModalShell, Pager, PasswordCriteriaInfo, usePagination } from "./ui-helpers";
import { formatDateTime } from "../utils/datetime";
import { isStrongPassword, PASSWORD_POLICY_MESSAGE } from "../utils/password";
import PinModal, { usePinGate } from "./PinModal";
import {
  MaterialTemplateModal,
  useMaterialTemplateModal,
} from "./MaterialTemplateModal";

const EMPTY_ROW = {
  Parameter: "",
  Units: "",
  "Lower Limit": "",
  "Upper Limit": "",
  "Data type": "Int",
  Caution: "No",
};

let specUiRowCounter = 0;
function makeUiRowId() {
  specUiRowCounter += 1;
  return `spec-row-${specUiRowCounter}`;
}

function withUiRowId(row) {
  if (row && row._uiRowId) return row;
  return { ...EMPTY_ROW, ...(row || {}), _uiRowId: makeUiRowId() };
}

function stripUiRowId(row) {
  const { _uiRowId, ...clean } = row || {};
  return clean;
}

function normalizeSpecRows(rows) {
  const list = Array.isArray(rows) && rows.length ? rows : [{ ...EMPTY_ROW }];
  return list.map(withUiRowId);
}

function cleanSpecRows(rows) {
  return normalizeSpecRows(rows).map(stripUiRowId);
}

const TYPES = [
  "Drugs",
  "Cosmetics",
  "Ayush",
  "Food",
  "Medical Devices",
  "Commodities",
  "Others",
];
const DEFAULT_THR = {
  Y: { criteria: "Nil", count: 0 },
  N: { criteria: "Nil", count: 0 },
  NA: { criteria: "Nil", count: 0 },
};
const CRITERIA = ["Max", "Min", "Equals", "Nil"];

// Pass I1: retain complex-material state and backend support while keeping the
// advanced control out of the Add Material UI until it is ready for release.
const COMPLEX_MATERIAL_UI_ENABLED = false;

function statusMeta(value) {
  const raw = String(value ?? "").toLowerCase();
  if (raw === "1" || raw === "true" || raw === "yes" || raw === "active")
    return { label: "Approved", cls: "status-approved", active: true };
  if (raw === "suspended")
    return { label: "Not approved", cls: "status-pending", active: false };
  if (raw === "deleted")
    return { label: "Removed", cls: "status-rejected", active: false };
  return {
    label: raw ? raw[0].toUpperCase() + raw.slice(1) : "Not approved",
    cls: "status-pending",
    active: false,
  };
}

function materialStatusMeta(value, approvedValue) {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (raw === "approved" || raw === "1" || raw === "true" || raw === "yes")
    return { status: "approved", label: "Approved", cls: "status-approved", active: true };
  if (raw === "rejected")
    return { status: "rejected", label: "Rejected", cls: "status-rejected", active: false };
  if (raw === "sent_back")
    return { status: "sent_back", label: "Reverted", cls: "status-pending", active: false };
  if (raw === "withdrawn")
    return { status: "withdrawn", label: "Withdrawn", cls: "status-rejected", active: false };
  if (raw === "pending")
    return { status: "pending", label: "Pending", cls: "status-pending", active: false };
  const fallback = String(approvedValue ?? "")
    .trim()
    .toLowerCase();
  if (["1", "true", "yes", "active"].includes(fallback))
    return { status: "approved", label: "Approved", cls: "status-approved", active: true };
  return { status: "pending", label: "Pending", cls: "status-pending", active: false };
}

const MATERIAL_STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "sent_back", label: "Reverted" },
  { value: "withdrawn", label: "Withdrawn" },
];

function isLocalDateInRange(value, range) {
  const raw = String(value || "").slice(0, 10);
  if (!raw) return true;
  if (range?.date_from && raw < range.date_from) return false;
  if (range?.date_to && raw > range.date_to) return false;
  return true;
}

function SpecEditor({ rows, setRows }) {
  const normalizedRows = React.useMemo(() => normalizeSpecRows(rows), [rows]);
  const [rowCount, setRowCount] = React.useState(normalizedRows.length || 1);

  React.useEffect(() => {
    if ((rows || []).some((row) => !row?._uiRowId) || !Array.isArray(rows) || rows.length === 0) {
      setRows((prev) => normalizeSpecRows(prev));
    }
  }, [rows, setRows]);

  React.useEffect(() => {
    setRowCount(normalizedRows.length || 1);
  }, [normalizedRows.length]);

  const addRow = () => setRows((prev) => [...normalizeSpecRows(prev), withUiRowId(EMPTY_ROW)]);
  const insertRowAfter = (index) =>
    setRows((prev) => {
      const list = normalizeSpecRows(prev);
      return [
        ...list.slice(0, index + 1),
        withUiRowId(EMPTY_ROW),
        ...list.slice(index + 1),
      ];
    });
  const delRow = (index) =>
    setRows((prev) => {
      const next = normalizeSpecRows(prev).filter((_, idx) => idx !== index);
      return next.length ? next : [withUiRowId(EMPTY_ROW)];
    });
  const moveRow = (index, direction) =>
    setRows((prev) => {
      const list = normalizeSpecRows(prev);
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= list.length) return list;
      const next = [...list];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
  const setCell = (index, key, value) =>
    setRows((prev) =>
      normalizeSpecRows(prev).map((row, idx) =>
        idx === index ? { ...row, [key]: value } : row,
      ),
    );
  const applyRowCount = () => {
    const next = Math.max(1, parseInt(rowCount || "1", 10) || 1);
    setRows((prev) => {
      const list = normalizeSpecRows(prev);
      if (next === list.length) return list;
      if (next < list.length) return list.slice(0, next);
      return [
        ...list,
        ...Array.from({ length: next - list.length }, () => withUiRowId(EMPTY_ROW)),
      ];
    });
  };

  return (
    <div className="table--card spec-editor-shell">
      <div className="spec-editor-header">
        <div>
          <h4>Specification rows</h4>
          <div className="helper-text">
            Set the row count directly, insert rows between parameters, or reorder rows.
          </div>
        </div>
        <div className="spec-editor-row-tools">
          <label>
            <span># Rows</span>
            <input
              type="number"
              min="1"
              value={rowCount}
              onChange={(e) => setRowCount(e.target.value)}
            />
          </label>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={applyRowCount}
          >
            Apply
          </button>
          <button className="btn btn-secondary" type="button" onClick={addRow}>
            Add row
          </button>
        </div>
      </div>
      <div className="table-scroll">
        <table className="table table--tight material-spec-table">
          <thead>
            <tr>
              <th>Parameter</th>
              <th>Units</th>
              <th>Lower limit</th>
              <th>Upper limit</th>
              <th>Data type</th>
              <th>Caution</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {normalizedRows.map((row, index) => (
              <tr key={row._uiRowId || `spec-row-${index}`}>
                <td>
                  <input
                    value={row.Parameter}
                    onChange={(e) =>
                      setCell(index, "Parameter", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    value={row.Units}
                    onChange={(e) => setCell(index, "Units", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    value={row["Lower Limit"]}
                    onChange={(e) =>
                      setCell(index, "Lower Limit", e.target.value)
                    }
                  />
                </td>
                <td>
                  <input
                    value={row["Upper Limit"]}
                    onChange={(e) =>
                      setCell(index, "Upper Limit", e.target.value)
                    }
                  />
                </td>
                <td>
                  <select
                    value={row["Data type"]}
                    onChange={(e) =>
                      setCell(index, "Data type", e.target.value)
                    }
                  >
                    <option>Int</option>
                    <option>Text</option>
                    <option>Date</option>
                  </select>
                </td>
                <td>
                  <select
                    value={row.Caution}
                    onChange={(e) => setCell(index, "Caution", e.target.value)}
                  >
                    <option>No</option>
                    <option>Yes</option>
                  </select>
                </td>
                <td>
                  <div className="table-actions table-actions--wrap">
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      onClick={() => insertRowAfter(index)}
                    >
                      Insert row
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveRow(index, -1)}
                    >
                      Move up
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      disabled={index === normalizedRows.length - 1}
                      onClick={() => moveRow(index, 1)}
                    >
                      Move down
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      onClick={() => delRow(index)}
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function normalizeParameterName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function specParameter(row) {
  return String(row?.Parameter || row?.parameter || "").trim();
}

function ParameterAliasMapper({
  currentRows,
  predecessorRows,
  selections,
  onChange,
  selectedSourceVersion,
  onSourceVersionChange,
  rowSourceVersions,
  onRowSourceVersionChange,
}) {
  const versionOptions = React.useMemo(() => {
    const versions = new Set(
      (Array.isArray(predecessorRows) ? predecessorRows : []).map((row) =>
        Number(row?._version ?? 0),
      ),
    );
    return Array.from(versions).sort((a, b) => b - a);
  }, [predecessorRows]);

  const defaultVersion = versionOptions[0] ?? 0;
  const selectedVersion = Number(
    selectedSourceVersion !== "" && selectedSourceVersion !== undefined
      ? selectedSourceVersion
      : defaultVersion,
  );
  const sourceRowsWithIndex = React.useMemo(
    () =>
      (Array.isArray(predecessorRows) ? predecessorRows : []).map((row, sourceIndex) => ({
        row,
        sourceIndex,
      })),
    [predecessorRows],
  );
  const versionedSourceRows = React.useMemo(
    () => sourceRowsWithIndex.filter(({ row }) => Number(row?._version ?? 0) === selectedVersion),
    [sourceRowsWithIndex, selectedVersion],
  );

  const rowVersionValue = React.useCallback(
    (index) => {
      const parsed = Number(rowSourceVersions?.[index]);
      return versionOptions.includes(parsed) ? parsed : defaultVersion;
    },
    [defaultVersion, rowSourceVersions, versionOptions],
  );

  if (!Array.isArray(predecessorRows) || predecessorRows.length === 0) {
    return (
      <div className="banner warn">
        Source template specifications could not be loaded. Replacement can still be submitted with unmapped parameters.
      </div>
    );
  }
  return (
    <div className="table--card material-lineage-card">
      <div className="spec-editor-header">
        <div>
          <h4>Replacement parameter links</h4>
          <div className="helper-text">
            Link each current specification row to a source parameter from one withdrawn material version at a time. Leave intentionally new parameters unmapped.
          </div>
        </div>
        <label className="field-inline">
          <span>Preview source version</span>
          <select
            value={String(selectedVersion)}
            onChange={(event) => onSourceVersionChange(event.target.value)}
          >
            {versionOptions.map((version) => (
              <option key={version} value={String(version)}>
                Version {version}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="table-scroll">
        <table className="table table--tight material-spec-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Source parameter</th>
              <th>Units</th>
              <th>LL</th>
              <th>UL</th>
              <th>Data type</th>
              <th>Caution</th>
            </tr>
          </thead>
          <tbody>
            {versionedSourceRows.map(({ row: sourceRow, sourceIndex }, displayIndex) => (
              <tr key={`source-preview-${sourceIndex}`}>
                <td>{displayIndex + 1}</td>
                <td>{sourceRow?._label || specParameter(sourceRow) || "—"}</td>
                <td>{sourceRow?.Units || "—"}</td>
                <td>{sourceRow?.["Lower Limit"] || "—"}</td>
                <td>{sourceRow?.["Upper Limit"] || "—"}</td>
                <td>{sourceRow?.["Data type"] || "—"}</td>
                <td>{sourceRow?.Caution || "—"}</td>
              </tr>
            ))}
            {versionedSourceRows.length === 0 ? (
              <tr>
                <td colSpan={7}>No source parameters found for this version.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="table-scroll" style={{ marginTop: 12 }}>
        <table className="table table--tight material-spec-table">
          <thead>
            <tr>
              <th>Current parameter</th>
              <th>Source version</th>
              <th>Source withdrawn parameter</th>
            </tr>
          </thead>
          <tbody>
            {(currentRows || []).map((row, index) => {
              const rowVersion = rowVersionValue(index);
              const rowSourceRows = sourceRowsWithIndex.filter(
                ({ row: sourceRow }) => Number(sourceRow?._version ?? 0) === rowVersion,
              );
              return (
                <tr key={`alias-${row?._uiRowId || index}`}>
                  <td>{specParameter(row) || `Row ${index + 1}`}</td>
                  <td>
                    <select
                      value={String(rowVersion)}
                      onChange={(event) => onRowSourceVersionChange(index, event.target.value)}
                    >
                      {versionOptions.map((version) => (
                        <option key={`row-${index}-version-${version}`} value={String(version)}>
                          Version {version}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={selections?.[index] ?? ""}
                      onChange={(event) => onChange(index, event.target.value)}
                    >
                      <option value="">New / unmapped parameter</option>
                      {rowSourceRows.map(({ row: sourceRow, sourceIndex }) => (
                        <option key={`source-${index}-${sourceIndex}`} value={String(sourceIndex)}>
                          {sourceRow?._label || specParameter(sourceRow) || `Source row ${sourceIndex + 1}`}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export default function VendorMaterialsWorkspace({ scopeLabel = "Admin" }) {
  const { current: org } = useOrg();
  const { user } = useAuth();
  const { t, tp } = useTerminology();
  const isSuperAdminScope = scopeLabel === "Super Admin";
  const [activeTab, setActiveTab] = React.useState("add-vendor");
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");

  const [vendorName, setVendorName] = React.useState("");
  const [vendorEmail, setVendorEmail] = React.useState("");
  const [vendorPassword, setVendorPassword] = React.useState("");

  const [, setVendorsOrg] = React.useState([]);
  const [, setVendorsGlobal] = React.useState([]);
  const [materialsByVendor, setMaterialsByVendor] = React.useState({});
  const [vendorRows, setVendorRows] = React.useState([]);
  const [settings, setSettings] = React.useState(null);
  const checklistEnabled = !!settings?.checklist_enabled;
  const complexMaterialSupported =
    String(settings?.model_choice || "LLM").toUpperCase() === "ML";
  const showComplexToggle =
    COMPLEX_MATERIAL_UI_ENABLED && complexMaterialSupported;

  const [selectedVendor, setSelectedVendor] = React.useState("");
  const [materialName, setMaterialName] = React.useState("");
  const [sku, setSku] = React.useState("");
  const [stdMrpRs, setStdMrpRs] = React.useState("");
  const [mtype, setMtype] = React.useState("Others");
  const [thrType, setThrType] = React.useState("Others");
  const [complexMaterial, setComplexMaterial] = React.useState(false);
  const [specRows, setSpecRows] = React.useState(() => [withUiRowId(EMPTY_ROW)]);
  const [thr, setThr] = React.useState({ ...DEFAULT_THR });
  const [mrpItems, setMrpItems] = React.useState([]);
  const [mrpSearch, setMrpSearch] = React.useState("");
  const [selectedMrpId, setSelectedMrpId] = React.useState("");
  const [mrpStd, setMrpStd] = React.useState("");
  const [mrpMin, setMrpMin] = React.useState("");
  const [mrpMax, setMrpMax] = React.useState("");
  const [vendorSearch, setVendorSearch] = React.useState("");
  const [materialSearch, setMaterialSearch] = React.useState("");
  const [materialStatusFilter, setMaterialStatusFilter] = React.useState("");
  const [materialDateDraft, setMaterialDateDraft] = React.useState({ date_from: "", date_to: "" });
  const [materialDateRange, setMaterialDateRange] = React.useState({ date_from: "", date_to: "" });
  const [vendorSort, setVendorSort] = React.useState("name");
  const [materialSort, setMaterialSort] = React.useState("vendor");
  const [editingVendorEmail, setEditingVendorEmail] = React.useState(null);
  const [emailSaving, setEmailSaving] = React.useState(false);
  const [editingMaterial, setEditingMaterial] = React.useState(null);
  const [replacementSourceMaterial, setReplacementSourceMaterial] = React.useState(null);
  const [replacementSourceSpecs, setReplacementSourceSpecs] = React.useState([]);
  const [selectedSourceVersion, setSelectedSourceVersion] = React.useState("");
  const [rowSourceVersions, setRowSourceVersions] = React.useState([]);
  const [parameterAliasSelections, setParameterAliasSelections] = React.useState([]);
  const [unlinkedReplacementSubmit, setUnlinkedReplacementSubmit] = React.useState(null);
  const pinGate = usePinGate();
  const templateModal = useMaterialTemplateModal();
  const aliasSelectionsByRowIdRef = React.useRef(new Map());
  const aliasVersionsByRowIdRef = React.useRef(new Map());

  const loadWorkspace = React.useCallback(async () => {
    const [vendorRes, poolRes, settingsRes, mrpRes] = await Promise.allSettled([
      api.get("/manager/list_vendor_materials"),
      api.get("/vendors/pool"),
      api.get("/orgs/settings"),
      api.get("/materials/mrp_config"),
    ]);

    if (vendorRes.status === "fulfilled") {
      const vendors = vendorRes.value.data?.vendors || [];
      setVendorRows(vendors);
      setVendorsOrg(
        vendors
          .map((item) => item.name || item.vendor || item.uid || "")
          .filter(Boolean),
      );
      setMaterialsByVendor(vendorRes.value.data?.materials_by_vendor || {});
    } else {
      setVendorRows([]);
      setVendorsOrg([]);
      setMaterialsByVendor({});
    }

    if (poolRes.status === "fulfilled") {
      setVendorsGlobal(
        (poolRes.value.data?.vendors || [])
          .map((item) => item.name || item.vendor || item)
          .filter(Boolean),
      );
    } else {
      setVendorsGlobal([]);
    }

    if (settingsRes.status === "fulfilled") {
      setSettings(settingsRes.value.data || {});
    }

    if (mrpRes.status === "fulfilled") {
      setMrpItems(mrpRes.value.data?.items || []);
    } else {
      setMrpItems([]);
    }
  }, []);

  React.useEffect(() => {
    loadWorkspace().catch(() => {});
  }, [loadWorkspace, org]);

  React.useEffect(() => {
    if (!checklistEnabled || !thrType) {
      setThr({ ...DEFAULT_THR });
      return;
    }
    api
      .get("/checklists/threshold", { params: { material_type: thrType } })
      .then((response) =>
        setThr(response.data?.threshold || { ...DEFAULT_THR }),
      )
      .catch(() => setThr({ ...DEFAULT_THR }));
  }, [thrType, checklistEnabled, org]);

  React.useEffect(() => {
    if (!complexMaterialSupported) setComplexMaterial(false);
  }, [complexMaterialSupported]);

  const sourceVersionOptions = React.useMemo(() => {
    const versions = new Set(
      (replacementSourceSpecs || []).map((row) => Number(row?._version ?? 0)),
    );
    return Array.from(versions).sort((a, b) => b - a);
  }, [replacementSourceSpecs]);

  const selectedSourceVersionValue = React.useMemo(() => {
    const parsed = Number(selectedSourceVersion);
    if (selectedSourceVersion !== "" && sourceVersionOptions.includes(parsed)) {
      return parsed;
    }
    return sourceVersionOptions[0] ?? 0;
  }, [selectedSourceVersion, sourceVersionOptions]);

  React.useEffect(() => {
    if (!replacementSourceMaterial) {
      if (selectedSourceVersion !== "") setSelectedSourceVersion("");
      return;
    }
    if (!sourceVersionOptions.length) return;
    if (!sourceVersionOptions.includes(Number(selectedSourceVersion))) {
      setSelectedSourceVersion(String(sourceVersionOptions[0]));
    }
  }, [replacementSourceMaterial, selectedSourceVersion, sourceVersionOptions]);

  React.useEffect(() => {
    if (!replacementSourceMaterial) {
      aliasSelectionsByRowIdRef.current = new Map();
      aliasVersionsByRowIdRef.current = new Map();
      setParameterAliasSelections([]);
      setRowSourceVersions([]);
      return;
    }
    const defaultVersion = sourceVersionOptions[0] ?? 0;
    const sourceRows = replacementSourceSpecs || [];
    const validVersion = (value) => {
      const parsed = Number(value);
      return sourceVersionOptions.includes(parsed) ? parsed : defaultVersion;
    };
    const sourceRowFor = (value) => {
      if (value === undefined || value === "") return null;
      const sourceRow = sourceRows[Number(value)];
      return sourceRow ? sourceRow : null;
    };
    const sourceVersionFor = (value) => Number(sourceRowFor(value)?._version ?? defaultVersion);

    setParameterAliasSelections((current) => {
      const nextSelections = [];
      const nextVersions = [];
      (specRows || []).forEach((row, index) => {
        const rowId = row?._uiRowId || "";
        const rowStoredSelection = rowId ? aliasSelectionsByRowIdRef.current.get(rowId) : undefined;
        const existingSelection = current?.[index];
        const storedVersion = rowId ? aliasVersionsByRowIdRef.current.get(rowId) : undefined;
        const preferredVersion = validVersion(storedVersion);

        const preservedSelection = sourceRowFor(rowStoredSelection)
          ? rowStoredSelection
          : sourceRowFor(existingSelection)
            ? existingSelection
            : "";
        if (preservedSelection !== "") {
          nextSelections[index] = String(preservedSelection);
          nextVersions[index] = String(sourceVersionFor(preservedSelection));
          return;
        }

        const wanted = normalizeParameterName(specParameter(row));
        if (!wanted) {
          nextSelections[index] = "";
          nextVersions[index] = String(preferredVersion);
          return;
        }
        const matchInPreferredVersion = sourceRows.findIndex(
          (sourceRow) =>
            Number(sourceRow?._version ?? 0) === preferredVersion &&
            normalizeParameterName(specParameter(sourceRow)) === wanted,
        );
        const matchIndex =
          matchInPreferredVersion >= 0
            ? matchInPreferredVersion
            : sourceRows.findIndex(
                (sourceRow) => normalizeParameterName(specParameter(sourceRow)) === wanted,
              );
        if (matchIndex >= 0) {
          nextSelections[index] = String(matchIndex);
          nextVersions[index] = String(sourceVersionFor(matchIndex));
        } else {
          nextSelections[index] = "";
          nextVersions[index] = String(preferredVersion);
        }
      });
      aliasSelectionsByRowIdRef.current = new Map(
        (specRows || [])
          .map((row, index) => [row?._uiRowId || "", nextSelections[index] || ""])
          .filter(([rowId]) => rowId),
      );
      aliasVersionsByRowIdRef.current = new Map(
        (specRows || [])
          .map((row, index) => [row?._uiRowId || "", nextVersions[index] || String(defaultVersion)])
          .filter(([rowId]) => rowId),
      );
      setRowSourceVersions(nextVersions);
      return nextSelections;
    });
  }, [replacementSourceMaterial, replacementSourceSpecs, sourceVersionOptions, specRows]);

  const vendorOptions = React.useMemo(
    () =>
      (vendorRows || [])
        .filter((item) => statusMeta(item.status ?? item.approved).active)
        .map((item) => item.name || item.vendor || item.uid || "")
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [vendorRows],
  );

  const materialFormVendorOptions = React.useMemo(() => {
    const values = new Set(vendorOptions);
    if (selectedVendor) values.add(selectedVendor);
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [vendorOptions, selectedVendor]);

  React.useEffect(() => {
    if (editingMaterial || replacementSourceMaterial) return;
    if (selectedVendor && !vendorOptions.includes(selectedVendor)) {
      setSelectedVendor(vendorOptions[0] || "");
    }
  }, [selectedVendor, vendorOptions, editingMaterial, replacementSourceMaterial]);

  const vendorTableRows = React.useMemo(() => {
    const needle = vendorSearch.trim().toLowerCase();
    const rows = (vendorRows || []).filter((item) => {
      if (!needle) return true;
      const row =
        `${item.name || item.vendor || ""} ${item.email || ""} ${item.status || item.approved || ""}`.toLowerCase();
      return row.includes(needle);
    });
    rows.sort((a, b) => {
      if (vendorSort === "status")
        return String(a.status || a.approved || "").localeCompare(
          String(b.status || b.approved || ""),
        );
      if (vendorSort === "created")
        return String(a.created_at || "").localeCompare(
          String(b.created_at || ""),
        );
      return String(a.name || a.vendor || "").localeCompare(
        String(b.name || b.vendor || ""),
      );
    });
    return rows;
  }, [vendorRows, vendorSearch, vendorSort]);

  const allMaterials = React.useMemo(() => {
    const rows = [];
    Object.entries(materialsByVendor || {}).forEach(([vendor, items]) => {
      (items || []).forEach((item) => rows.push({ vendor, ...item }));
    });
    return rows;
  }, [materialsByVendor]);

  const materialTableRows = React.useMemo(() => {
    const needle = materialSearch.trim().toLowerCase();
    const rows = allMaterials.filter((item) => {
      const state = materialStatusMeta(item.approval_status, item.approved);
      if (materialStatusFilter && state.status !== materialStatusFilter) return false;
      if (!isLocalDateInRange(item.created_at, materialDateRange)) return false;
      if (!needle) return true;
      const searchable = [
        item.vendor,
        item.material,
        item.sku,
        isSuperAdminScope ? item.material_id : "",
        item.type,
        item.std_mrp_rs,
        state.label,
        item.approval_status,
        item.last_decision_comment,
      ];
      return searchable.join(" ").toLowerCase().includes(needle);
    });
    rows.sort((a, b) => {
      if (materialSort === "material")
        return `${a.material || ""}`.localeCompare(`${b.material || ""}`);
      if (materialSort === "sku")
        return `${a.sku || ""}`.localeCompare(`${b.sku || ""}`);
      if (materialSort === "created")
        return `${a.created_at || ""}`.localeCompare(`${b.created_at || ""}`);
      return `${a.vendor || ""}-${a.sku || ""}`.localeCompare(
        `${b.vendor || ""}-${b.sku || ""}`,
      );
    });
    return rows;
  }, [allMaterials, materialSearch, materialStatusFilter, materialDateRange, materialSort, isSuperAdminScope]);

  const mrpFilteredItems = React.useMemo(() => {
    const needle = mrpSearch.trim().toLowerCase();
    const rows = (mrpItems || []).filter((item) => {
      if (!needle) return true;
      const searchable = [
        item.vendor,
        item.material,
        item.sku,
        isSuperAdminScope ? item.material_id : "",
        item.type || item.category,
        item.std_mrp_rs,
      ];
      return searchable.join(" ").toLowerCase().includes(needle);
    });
    rows.sort((a, b) =>
      `${a.vendor || ""}-${a.material || ""}-${a.sku || ""}`.localeCompare(
        `${b.vendor || ""}-${b.material || ""}-${b.sku || ""}`,
      ),
    );
    return rows;
  }, [mrpItems, mrpSearch, isSuperAdminScope]);

  const selectedMrpItem = React.useMemo(
    () =>
      (mrpItems || []).find(
        (item) =>
          String(item.material_id || "") === String(selectedMrpId || ""),
      ) || null,
    [mrpItems, selectedMrpId],
  );

  React.useEffect(() => {
    if (!selectedMrpItem) {
      setMrpStd("");
      setMrpMin("");
      setMrpMax("");
      return;
    }
    setMrpStd(selectedMrpItem.std_mrp_rs ?? "");
    setMrpMin(selectedMrpItem.min_mrp_rs ?? "");
    setMrpMax(selectedMrpItem.max_mrp_rs ?? "");
  }, [selectedMrpItem]);

  const vendorPager = usePagination(vendorTableRows, 10);
  const materialPager = usePagination(materialTableRows, 10);

  const showFeedback = (nextMessage = "", nextError = "") => {
    setMessage(nextMessage);
    setError(nextError);
  };

  const performSubmitVendor = async (payload) => {
    try {
      const response = await postForm("/user/add_vendor", payload);
      setVendorName("");
      setVendorEmail("");
      setVendorPassword("");
      showFeedback(
        response.data?.message ||
          `${t("vendor")} submitted for manager approval.`,
        "",
      );
      await loadWorkspace();
    } catch (err) {
      showFeedback(
        "",
        err?.response?.data?.error ||
          `Failed to add ${t("vendor").toLowerCase()}.`,
      );
    }
  };

  const submitVendor = () => {
    showFeedback();
    if (!vendorName || !vendorEmail || !vendorPassword) {
      showFeedback(
        "",
        `${t("vendor")} name, email, and password are required.`,
      );
      return;
    }
    if (!isStrongPassword(vendorPassword)) {
      showFeedback("", PASSWORD_POLICY_MESSAGE);
      return;
    }
    const payload = {
      vendor_name: vendorName,
      email: vendorEmail,
      password: vendorPassword,
    };
    pinGate.askPinThen(() => performSubmitVendor(payload));
  };

  const startVendorEmailEdit = (item) => {
    const name = item.name || item.vendor || item.uid || "";
    setEditingVendorEmail({ name, email: item.email || "" });
    showFeedback();
  };

  const cancelVendorEmailEdit = () => {
    setEditingVendorEmail(null);
    setEmailSaving(false);
  };

  const saveVendorEmail = async () => {
    if (!editingVendorEmail?.name || emailSaving) return;
    const nextEmail = String(editingVendorEmail.email || "").trim();
    if (!nextEmail) {
      showFeedback("", `${t("vendor")} email is required.`);
      return;
    }
    setEmailSaving(true);
    showFeedback();
    try {
      const response = await postForm("/user/update_vendor_email", {
        vendor_name: editingVendorEmail.name,
        email: nextEmail,
      });
      setEditingVendorEmail(null);
      showFeedback(
        response.data?.message || `${t("vendor")} email updated.`,
        "",
      );
      await loadWorkspace();
    } catch (err) {
      showFeedback(
        "",
        err?.response?.data?.error ||
          `Failed to update ${t("vendor").toLowerCase()} email.`,
      );
    } finally {
      setEmailSaving(false);
    }
  };


  const resetMaterialForm = () => {
    setEditingMaterial(null);
    setReplacementSourceMaterial(null);
    setReplacementSourceSpecs([]);
    setSelectedSourceVersion("");
    setRowSourceVersions([]);
    setParameterAliasSelections([]);
    aliasSelectionsByRowIdRef.current = new Map();
    aliasVersionsByRowIdRef.current = new Map();
    setSelectedVendor(vendorOptions[0] || "");
    setMaterialName("");
    setSku("");
    setStdMrpRs("");
    setMtype("Others");
    setThrType("Others");
    setComplexMaterial(false);
    setSpecRows([withUiRowId(EMPTY_ROW)]);
  };

  const loadTemplateForItem = async (item) => {
    const response = await api.get("/materials/template", {
      params: {
        material_id: item?.material_id || undefined,
        vendor: item?.vendor || undefined,
        sku: item?.sku || undefined,
      },
    });
    return response.data || {};
  };

  const startMaterialResubmitEdit = async (item) => {
    showFeedback();
    try {
      const data = await loadTemplateForItem(item);
      const tpl = data.template || {};
      const row = data.material || item || {};
      setEditingMaterial(row);
      setReplacementSourceMaterial(null);
      setReplacementSourceSpecs([]);
      setSelectedSourceVersion("");
      setParameterAliasSelections([]);
      setSelectedVendor(row.vendor || tpl["Vendor Name"] || item.vendor || "");
      setMaterialName(row.material || tpl["Material Name"] || item.material || "");
      setSku(row.sku || tpl.SKU || item.sku || "");
      setStdMrpRs(row.std_mrp_rs ?? tpl["Std MRP Rs."] ?? "");
      setMtype(row.type || tpl.Type || "Others");
      setThrType(row.threshold_type || tpl["Threshold Type"] || row.type || tpl.Type || "Others");
      setComplexMaterial(
        ["1", "true", "yes"].includes(String(row.complex ?? tpl.complex ?? tpl.Complex ?? "").toLowerCase()),
      );
      const specs = Array.isArray(tpl.Specifications) && tpl.Specifications.length ? tpl.Specifications : [{ ...EMPTY_ROW }];
      setSpecRows(normalizeSpecRows(specs));
      setActiveTab("add-material");
      showFeedback("Editing reverted material. Resubmission will preserve the same hidden material identity.", "");
    } catch (err) {
      showFeedback("", err?.response?.data?.error || "Unable to load material template for editing.");
    }
  };

  const startMaterialReplacement = async (item) => {
    showFeedback();
    try {
      const data = await loadTemplateForItem(item);
      const tpl = data.template || {};
      const row = data.material || item || {};
      const specs = Array.isArray(tpl.Specifications) && tpl.Specifications.length ? tpl.Specifications : [{ ...EMPTY_ROW }];
      const sourceOptions = [];
      (Array.isArray(data.lineage_chain) && data.lineage_chain.length ? data.lineage_chain : [{ material: row, template: tpl, version: row.version || 0 }]).forEach((entry) => {
        const sourceMaterial = entry.material || row || {};
        const sourceVersion = Number(entry.version ?? sourceMaterial.version ?? 0);
        const sourceSpecs = Array.isArray(entry.template?.Specifications) ? entry.template.Specifications : [];
        sourceSpecs.forEach((sourceRow, sourceIndex) => {
          const paramName = specParameter(sourceRow) || `Source row ${sourceIndex + 1}`;
          sourceOptions.push({
            ...EMPTY_ROW,
            ...(sourceRow || {}),
            _material_id: sourceMaterial.material_id || entry.material_id || "",
            _version: sourceVersion,
            _source_index: sourceIndex,
            _label: `${paramName} (version ${sourceVersion})`,
          });
        });
      });
      setEditingMaterial(null);
      setReplacementSourceMaterial(row);
      const nextSourceSpecs = sourceOptions.length
        ? sourceOptions
        : specs.map((r, idx) => ({
            ...EMPTY_ROW,
            ...(r || {}),
            _material_id: row.material_id || "",
            _version: row.version || 0,
            _source_index: idx,
          }));
      const nextVersions = nextSourceSpecs.map((sourceRow) => Number(sourceRow?._version ?? 0));
      setReplacementSourceSpecs(nextSourceSpecs);
      setSelectedSourceVersion(String(nextVersions.length ? Math.max(...nextVersions) : 0));
      setRowSourceVersions([]);
      setSelectedVendor(row.vendor || tpl["Vendor Name"] || item.vendor || "");
      setMaterialName(row.material || tpl["Material Name"] || item.material || "");
      setSku(row.sku || tpl.SKU || item.sku || "");
      setStdMrpRs(row.std_mrp_rs ?? tpl["Std MRP Rs."] ?? "");
      setMtype(row.type || tpl.Type || "Others");
      setThrType(row.threshold_type || tpl["Threshold Type"] || row.type || tpl.Type || "Others");
      setComplexMaterial(
        ["1", "true", "yes"].includes(String(row.complex ?? tpl.complex ?? tpl.Complex ?? "").toLowerCase()),
      );
      setSpecRows(normalizeSpecRows(specs));
      setActiveTab("add-material");
      showFeedback("Creating a replacement for a withdrawn material. A new material identity will be submitted for manager approval.", "");
    } catch (err) {
      showFeedback("", err?.response?.data?.error || "Unable to load withdrawn material template for replacement.");
    }
  };

  const setAliasSelection = (index, value) => {
    setParameterAliasSelections((current) => {
      const next = [...(current || [])];
      next[index] = value;
      const rowId = specRows?.[index]?._uiRowId || "";
      if (rowId) aliasSelectionsByRowIdRef.current.set(rowId, value);
      const sourceRow = value !== "" ? replacementSourceSpecs?.[Number(value)] : null;
      if (sourceRow) {
        const version = String(Number(sourceRow?._version ?? 0));
        if (rowId) aliasVersionsByRowIdRef.current.set(rowId, version);
        setRowSourceVersions((versions) => {
          const nextVersions = [...(versions || [])];
          nextVersions[index] = version;
          return nextVersions;
        });
      }
      return next;
    });
  };

  const setRowSourceVersion = (index, value) => {
    const version = String(value);
    const rowId = specRows?.[index]?._uiRowId || "";
    if (rowId) {
      aliasVersionsByRowIdRef.current.set(rowId, version);
      aliasSelectionsByRowIdRef.current.set(rowId, "");
    }
    setRowSourceVersions((current) => {
      const next = [...(current || [])];
      next[index] = version;
      return next;
    });
    setParameterAliasSelections((current) => {
      const next = [...(current || [])];
      next[index] = "";
      return next;
    });
  };

  const buildParameterAliases = () => {
    if (!replacementSourceMaterial) return [];
    const predecessorId = replacementSourceMaterial?.material_id || "";
    return (specRows || [])
      .map((row, index) => {
        const sourceIndexText = parameterAliasSelections?.[index];
        if (sourceIndexText === undefined || sourceIndexText === "") return null;
        const sourceIndex = Number(sourceIndexText);
        const sourceRow = replacementSourceSpecs?.[sourceIndex];
        if (!sourceRow) return null;
        const sourceMaterialId = String(sourceRow._material_id || predecessorId || "");
        const sourceRowIndex = Number.isFinite(Number(sourceRow._source_index)) ? Number(sourceRow._source_index) : sourceIndex;
        return {
          current_index: index,
          current_parameter: specParameter(row),
          predecessor_material_id: sourceMaterialId,
          predecessor_index: sourceRowIndex,
          predecessor_parameter: specParameter(sourceRow),
          source_material_id: sourceMaterialId,
          source_version: Number(sourceRow._version ?? 0),
          source_index: sourceRowIndex,
          source_parameter: specParameter(sourceRow),
        };
      })
      .filter(Boolean);
  };

  const hasUnlinkedReplacementRows = React.useCallback(() => {
    if (!replacementSourceMaterial) return false;
    return (specRows || []).some((row, index) => specParameter(row) && !String(parameterAliasSelections?.[index] ?? "").trim());
  }, [replacementSourceMaterial, specRows, parameterAliasSelections]);

  const cancelMaterialEdit = () => {
    const wasReplacement = !!replacementSourceMaterial;
    resetMaterialForm();
    showFeedback(wasReplacement ? "Replacement creation cancelled." : "Reverted edit cancelled.", "");
  };

  const performSubmitMaterial = async ({ payload, editingMaterialId, replacementSourceId, parameterAliases }) => {
    try {
      if (editingMaterialId) {
        await postForm("/user/resubmit_material", {
          ...payload,
          material_id: editingMaterialId,
          actor: scopeLabel,
        });
      } else {
        await postForm("/user/add_material", {
          ...payload,
          actor: scopeLabel,
          ...(replacementSourceId
            ? {
                predecessor_material_id: replacementSourceId,
                parameter_aliases: JSON.stringify(parameterAliases || []),
              }
            : {}),
        });
      }
      resetMaterialForm();
      showFeedback(
        editingMaterialId
          ? `${t("material")} resubmitted for manager approval.`
          : replacementSourceId
            ? `Replacement ${t("material").toLowerCase()} submitted for manager approval.`
            : `${t("material")} submitted for manager approval.`,
        "",
      );
      await loadWorkspace();
    } catch (err) {
      showFeedback("", err?.response?.data?.error || "Failed to add material.");
    }
  };

  const runMaterialSubmitAfterConfirm = (submitArgs) => {
    pinGate.askPinThen(() => performSubmitMaterial(submitArgs));
  };

  const submitMaterial = () => {
    showFeedback();
    if (!selectedVendor || !materialName || !sku) {
      showFeedback(
        "",
        `${t("vendor")}, ${t("material").toLowerCase()} name, and SKU are required.`,
      );
      return;
    }
    const payload = {
      vendor_name: selectedVendor,
      material_name: materialName,
      sku,
      std_mrp_rs: stdMrpRs,
      table_data: JSON.stringify(cleanSpecRows(specRows)),
      mtype,
      threshold_type: thrType || mtype || "Others",
      ...(showComplexToggle ? { complex: complexMaterial ? "1" : "0" } : {}),
    };
    const editingMaterialId = editingMaterial?.material_id || "";
    const replacementSourceId = replacementSourceMaterial?.material_id || "";
    const parameterAliases = buildParameterAliases();
    const submitArgs = { payload, editingMaterialId, replacementSourceId, parameterAliases };
    if (replacementSourceId && hasUnlinkedReplacementRows()) {
      setUnlinkedReplacementSubmit(submitArgs);
      return;
    }
    runMaterialSubmitAfterConfirm(submitArgs);
  };

  const saveThresholds = async () => {
    showFeedback();
    try {
      const form = new FormData();
      form.append("material_type", thrType || "Others");
      form.append("cfg", JSON.stringify(thr));
      await api.post("/checklists/threshold", form);
      showFeedback("Threshold configuration saved.", "");
    } catch {
      showFeedback("", "Failed to save threshold configuration.");
    }
  };

  const saveMrpSettings = async () => {
    showFeedback();
    if (!selectedMrpId) {
      showFeedback("", "Select a material before saving MRP settings.");
      return;
    }
    try {
      const form = new FormData();
      form.append("material_id", selectedMrpId);
      form.append("std_mrp_rs", mrpStd);
      form.append("min_mrp_rs", mrpMin);
      form.append("max_mrp_rs", mrpMax);
      const response = await api.post("/materials/mrp_threshold", form);
      const saved = response.data?.item;
      if (saved) {
        setMrpItems((current) =>
          (current || []).map((item) =>
            String(item.material_id || "") === String(saved.material_id || "")
              ? saved
              : item,
          ),
        );
        setMaterialsByVendor((current) => {
          const next = { ...(current || {}) };
          Object.keys(next).forEach((vendor) => {
            next[vendor] = (next[vendor] || []).map((item) =>
              String(item.material_id || "") === String(saved.material_id || "")
                ? { ...item, std_mrp_rs: saved.std_mrp_rs ?? "" }
                : item,
            );
          });
          return next;
        });
      }
      showFeedback(`${t("material")} MRP settings saved.`, "");
    } catch (err) {
      showFeedback(
        "",
        err?.response?.data?.error ||
          `Failed to save ${t("material")} MRP settings.`,
      );
    }
  };

  return (
    <div className="page">
      <div className="tabs page-tabs vendor-tabs">
        {[
          ["add-vendor", `Add ${t("vendor")}`],
          ["vendor-library", `${t("vendor")} Library`],
          ["add-material", `Add ${t("material")}`],
          ["material-library", `${t("material")} Library`],
          ["thresholds", "Thresholds"],
        ].map(([key, label]) => (
          <button
            key={key}
            className={activeTab === key ? "chip chip-on" : "chip"}
            type="button"
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {message ? <div className="banner success">{message}</div> : null}
      {error ? <div className="banner error">{error}</div> : null}

      {activeTab === "add-vendor" ? (
        <div className="section vendor-form-card">
          <div className="page-header-row">
            <div>
              <h3>Add {t("vendor").toLowerCase()}</h3>
              <div className="helper-text">
                Create {t("vendor").toLowerCase()} access.
              </div>
            </div>
          </div>
          <div className="form-grid vendor-entry-grid">
            <div className="field span-4">
              <label>
                {t("vendor")} name
                <input
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                />
              </label>
            </div>
            <div className="field span-4">
              <label>
                Email
                <input
                  value={vendorEmail}
                  onChange={(e) => setVendorEmail(e.target.value)}
                />
              </label>
            </div>
            <div className="field span-4 password-field">
              <label>
                <span className="field-label-inline">
                  <span>Password</span>
                  <PasswordCriteriaInfo />
                </span>
                <input
                  type="password"
                  value={vendorPassword}
                  onChange={(e) => setVendorPassword(e.target.value)}
                />
              </label>
            </div>
          </div>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={submitVendor}
            >
              Add {t("vendor")}
            </button>
          </div>
        </div>
      ) : null}

      {activeTab === "vendor-library" ? (
        <div className="section">
          <div className="page-header-row">
            <div>
              <h3>
                {scopeLabel} {tp("vendor").toLowerCase()}
              </h3>
              <div className="helper-text">
                Authoritative {t("vendor").toLowerCase()} list for this
                workspace.
              </div>
            </div>
          </div>
          <div className="filters-bar vendor-toolbar">
            <label className="field-grow">
              <span>Search {tp("vendor").toLowerCase()}</span>
              <input
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                placeholder={`Search ${t("vendor").toLowerCase()} name, email, or status`}
              />
            </label>
            <label>
              <span>Sort {tp("vendor").toLowerCase()}</span>
              <select
                value={vendorSort}
                onChange={(e) => setVendorSort(e.target.value)}
              >
                <option value="name">Name</option>
                <option value="status">Status</option>
                <option value="created">Created</option>
              </select>
            </label>
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense table--fixed vendor-table">
                <thead>
                  <tr>
                    <th>{t("vendor")}</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorPager.pageData.map((item, index) => {
                    const state = statusMeta(item.status ?? item.approved);
                    return (
                      <tr key={`${item.name || item.vendor}-${index}`}>
                        <td>
                          <span className="status-primary-text">
                            {item.name || item.vendor || "—"}
                          </span>
                        </td>
                        <td>
                          {editingVendorEmail?.name ===
                          (item.name || item.vendor || item.uid || "") ? (
                            <div className="inline-edit-row">
                              <input
                                value={editingVendorEmail.email}
                                onChange={(event) =>
                                  setEditingVendorEmail((prev) => ({
                                    ...(prev || {}),
                                    email: event.target.value,
                                  }))
                                }
                                placeholder="vendor@example.com"
                              />
                              <button
                                className="btn btn-primary btn-sm"
                                type="button"
                                disabled={emailSaving}
                                onClick={saveVendorEmail}
                              >
                                {emailSaving ? "Saving..." : "Save"}
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                type="button"
                                disabled={emailSaving}
                                onClick={cancelVendorEmailEdit}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <>
                              <span>{item.email || "—"}</span>
                              {Number(item.email_verified ?? 1) === 0 ? (
                                <span className="status-pill status-pending ml-2">
                                  Unverified
                                </span>
                              ) : (
                                <span className="status-pill status-approved ml-2">
                                  Verified
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          <span className={`status-pill ${state.cls}`}>
                            {state.label}
                          </span>
                        </td>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            type="button"
                            onClick={() => startVendorEmailEdit(item)}
                          >
                            Edit email
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {vendorTableRows.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        No {tp("vendor").toLowerCase()} found for this
                        workspace.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <Pager {...vendorPager} />
          </div>
        </div>
      ) : null}

      {activeTab === "add-material" ? (
        <div className="section material-form-card">
          <div className="page-header-row">
            <div>
              <h3>{editingMaterial ? `Edit reverted ${t("material").toLowerCase()}` : replacementSourceMaterial ? `Create replacement ${t("material").toLowerCase()}` : `Add ${t("material").toLowerCase()}`}</h3>
              <div className="helper-text">
                {editingMaterial
                  ? "Update the stored values and specifications, then resubmit the same material identity for manager review."
                  : replacementSourceMaterial
                    ? "This creates a new hidden material identity from a withdrawn source and submits it as Pending for manager approval."
                    : <>Use the full row width for {t("material").toLowerCase()} setup, threshold selection, and specification entry. Only approved {tp("vendor").toLowerCase()} appear here.</>}
              </div>
            </div>
          </div>
          <div className="form-grid material-entry-grid vendor-entry-grid--material">
            <div className="field span-4">
              <label>
                {t("vendor")}
                <select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                >
                  <option value="" />
                  {materialFormVendorOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="field span-4">
              <label>
                {t("material")} name
                <input
                  value={materialName}
                  onChange={(e) => setMaterialName(e.target.value)}
                />
              </label>
            </div>
            <div className="field span-4">
              <label>
                SKU
                <input value={sku} onChange={(e) => setSku(e.target.value)} />
              </label>
            </div>
            <div className="field span-4">
              <label>
                Std MRP Rs.
                <input
                  value={stdMrpRs}
                  onChange={(e) => setStdMrpRs(e.target.value)}
                  placeholder="Optional, e.g. 100"
                />
              </label>
              <div className="helper-text">
                Used for MRP review indicators and threshold flagging.
              </div>
            </div>
            <div className="field span-4">
              <label>
                {t("type")}
                <select
                  value={mtype}
                  onChange={(e) => {
                    setMtype(e.target.value);
                    setThrType(e.target.value);
                  }}
                >
                  {TYPES.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </label>
            </div>
            {checklistEnabled ? (
              <div className="field span-4">
                <label>
                  Apply thresholds of
                  <select
                    value={thrType}
                    onChange={(e) => setThrType(e.target.value)}
                  >
                    {TYPES.map((type) => (
                      <option key={type}>{type}</option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
            {showComplexToggle ? (
              <div className="field span-4 material-intent-row">
                <label className="checkbox-row checkbox-tile">
                  <input
                    type="checkbox"
                    checked={complexMaterial}
                    onChange={(e) => setComplexMaterial(e.target.checked)}
                  />
                  <span>
                    Complex {t("material").toLowerCase()} for additonal system
                    check
                  </span>
                </label>
              </div>
            ) : null}
          </div>
          <SpecEditor rows={specRows} setRows={setSpecRows} />
          {replacementSourceMaterial ? (
            <ParameterAliasMapper
              currentRows={specRows}
              predecessorRows={replacementSourceSpecs}
              selections={parameterAliasSelections}
              onChange={setAliasSelection}
              selectedSourceVersion={selectedSourceVersionValue}
              onSourceVersionChange={setSelectedSourceVersion}
              rowSourceVersions={rowSourceVersions}
              onRowSourceVersionChange={setRowSourceVersion}
            />
          ) : null}
          <div className="card-actions">
            {editingMaterial || replacementSourceMaterial ? (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={cancelMaterialEdit}
              >
                {replacementSourceMaterial ? "Cancel replacement" : "Cancel edit"}
              </button>
            ) : null}
            <button
              className="btn btn-primary"
              type="button"
              onClick={submitMaterial}
            >
              {editingMaterial ? "Resubmit for approval" : replacementSourceMaterial ? "Create replacement for approval" : <>Add {t("material")}</>}
            </button>
          </div>
        </div>
      ) : null}

      {activeTab === "material-library" ? (
        <div className="section">
          <div className="page-header-row">
            <div>
              <h3>{t("material")} library</h3>
              <div className="helper-text">
                Search and sort this workspace’s {t("material").toLowerCase()}
                setup.
              </div>
            </div>
          </div>
          <div className="filters-bar vendor-toolbar">
            <label className="field-grow">
              <span>Search {tp("material").toLowerCase()}</span>
              <input
                value={materialSearch}
                onChange={(e) => setMaterialSearch(e.target.value)}
                placeholder={
                  isSuperAdminScope
                    ? `Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ID, ${t("type").toLowerCase()}, or Std MRP`
                    : `Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("type").toLowerCase()}, or Std MRP`
                }
              />
            </label>
            <label>
              <span>Status</span>
              <select value={materialStatusFilter} onChange={(e) => setMaterialStatusFilter(e.target.value)}>
                {MATERIAL_STATUS_FILTERS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <DateRangePicker
              value={materialDateDraft}
              onChange={setMaterialDateDraft}
              onApply={() => setMaterialDateRange({ ...materialDateDraft })}
              onClear={(empty) => { setMaterialDateDraft(empty); setMaterialDateRange(empty); }}
            />
            <label>
              <span>Sort {tp("material").toLowerCase()}</span>
              <select
                value={materialSort}
                onChange={(e) => setMaterialSort(e.target.value)}
              >
                <option value="vendor">{t("vendor")}</option>
                <option value="material">{t("material")}</option>
                <option value="sku">SKU</option>
                <option value="created">Created</option>
              </select>
            </label>
          </div>
          <div className="helper-text" style={{ marginBottom: 10 }}>
            Click a {t("material").toLowerCase()} row to view the template.
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense table--fixed material-table">
                <thead>
                  <tr>
                    <th>{isSuperAdminScope ? "ID" : "S.NO"}</th>
                    <th>{t("vendor")}</th>
                    <th>{t("material")}</th>
                    <th>SKU</th>
                    <th>Std MRP Rs.</th>
                    <th>{t("type")}</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Decision Comment</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {materialPager.pageData.map((item, index) => {
                    const state = materialStatusMeta(
                      item.approval_status,
                      item.approved,
                    );
                    const rowKey = `${item.vendor}-${item.material_id || item.sku}-${index}`;
                    return (
                      <tr
                        key={rowKey}
                        className="clickable-row material-template-row"
                        tabIndex={0}
                        onClick={() => templateModal.openTemplate(item)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            templateModal.openTemplate(item);
                          }
                        }}
                      >
                        <td>
                          {isSuperAdminScope ? (
                            <span className="material-id-badge">
                              {item.material_id || "—"}
                            </span>
                          ) : (
                            materialPager.start + index + 1
                          )}
                        </td>
                        <td>{item.vendor}</td>
                        <td>
                          <span className="status-primary-text">
                            {item.material}
                          </span>
                          {item.predecessor_material_id ? (
                            <span className="status-pill status-pending ml-2">Replacement</span>
                          ) : null}
                        </td>
                        <td>{item.sku}</td>
                        <td>{item.std_mrp_rs || "—"}</td>
                        <td>{item.type || item.mtype || "Others"}</td>
                        <td>
                          <span className={`status-pill ${state.cls}`}>
                            {state.label}
                          </span>
                        </td>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>{item.last_decision_comment || "—"}</td>
                        <td>
                          <div className="table-actions">
                            {state.status === "sent_back" ? (
                              <button
                                className="btn btn-primary btn-sm"
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  startMaterialResubmitEdit(item);
                                }}
                              >
                                Edit / Resubmit
                              </button>
                            ) : null}
                            {state.status === "withdrawn" ? (
                              item.replaced_by_material_id ? (
                                <span className="helper-text">Replaced by: {formatDateTime(item.replaced_at)}</span>
                              ) : (
                                <button
                                  className="btn btn-primary btn-sm"
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    startMaterialReplacement(item);
                                  }}
                                >
                                  Create Replacement
                                </button>
                              )
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {materialTableRows.length === 0 ? (
                    <tr>
                      <td colSpan={10}>
                        No {tp("material").toLowerCase()} found for this
                        workspace.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <Pager {...materialPager} />
          </div>
        </div>
      ) : null}

      {activeTab === "thresholds" ? (
        <div className="section">
          <div className="page-header-row">
            <div>
              <h3>Checklist threshold configuration</h3>
              <div className="helper-text">
                Adjust threshold logic per {t("material").toLowerCase()}{" "}
                {t("type").toLowerCase()}.
              </div>
            </div>
          </div>
          <div className="form-grid">
            <div className="field span-6">
              <label>
                {t("material")} {t("type").toLowerCase()}
                <select
                  value={thrType}
                  onChange={(e) => setThrType(e.target.value)}
                >
                  {TYPES.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--tight table--fixed">
                <thead>
                  <tr>
                    <th>Answer bucket</th>
                    <th>Criteria</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(thr || {}).map(([key, value]) => (
                    <tr key={key}>
                      <td>{key}</td>
                      <td>
                        <select
                          value={value.criteria || "Nil"}
                          onChange={(e) =>
                            setThr((prev) => ({
                              ...prev,
                              [key]: {
                                ...(prev[key] || {}),
                                criteria: e.target.value,
                              },
                            }))
                          }
                        >
                          {CRITERIA.map((criterion) => (
                            <option key={criterion}>{criterion}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={value.count ?? 0}
                          onChange={(e) =>
                            setThr((prev) => ({
                              ...prev,
                              [key]: {
                                ...(prev[key] || {}),
                                count: Math.max(
                                  0,
                                  parseInt(e.target.value || "0", 10),
                                ),
                              },
                            }))
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={saveThresholds}
            >
              Save thresholds
            </button>
          </div>

          <div className="section" style={{ marginTop: 18 }}>
            <div className="page-header-row">
              <div>
                <h3>{t("material")} MRP Threshold Configuration</h3>
                <div className="helper-text">
                  Configure Std MRP and optional minimum/maximum MRP checks per{" "}
                  {t("material").toLowerCase()}. Blank Std MRP clears it; blank
                  min and max means no threshold.
                </div>
              </div>
            </div>
            <div className="form-grid">
              <div className="field span-6">
                <label>
                  Search {tp("material").toLowerCase()}
                  <input
                    value={mrpSearch}
                    onChange={(e) => setMrpSearch(e.target.value)}
                    placeholder={
                      isSuperAdminScope
                        ? `Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ID, ${t("type").toLowerCase()}, or Std MRP`
                        : `Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, SKU, ${t("type").toLowerCase()}, or Std MRP`
                    }
                  />
                </label>
              </div>
              <div className="field span-6">
                <label>
                  Select {t("material").toLowerCase()}
                  <select
                    value={selectedMrpId}
                    onChange={(e) => setSelectedMrpId(e.target.value)}
                  >
                    <option value="" />
                    {mrpFilteredItems.map((item) => (
                      <option
                        key={item.material_id || `${item.vendor}-${item.sku}`}
                        value={item.material_id}
                      >
                        {item.vendor} — {item.material} — {item.sku}{" "}
                        {item.std_mrp_rs ? `(Std ₹${item.std_mrp_rs})` : ""}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {selectedMrpItem ? (
              <>
                <div className="table--card" style={{ marginTop: 12 }}>
                  <div className="table-scroll">
                    <table className="table table--dense">
                      <tbody>
                        <tr>
                          <td>
                            <b>{t("vendor")}</b>
                          </td>
                          <td>{selectedMrpItem.vendor || "—"}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>{t("material")}</b>
                          </td>
                          <td>{selectedMrpItem.material || "—"}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>SKU / Pack Size</b>
                          </td>
                          <td>{selectedMrpItem.sku || "—"}</td>
                        </tr>
                        <tr>
                          <td>
                            <b>{t("type")}</b>
                          </td>
                          <td>
                            {selectedMrpItem.category ||
                              selectedMrpItem.type ||
                              "—"}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <b>Std MRP</b>
                          </td>
                          <td>{selectedMrpItem.std_mrp_rs || "—"}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="form-grid" style={{ marginTop: 12 }}>
                  <div className="field span-4">
                    <label>
                      Std MRP Rs.
                      <input
                        value={mrpStd}
                        onChange={(e) => setMrpStd(e.target.value)}
                        placeholder="Blank to clear"
                      />
                    </label>
                  </div>
                  <div className="field span-4">
                    <label>
                      Minimum MRP
                      <input
                        value={mrpMin}
                        onChange={(e) => setMrpMin(e.target.value)}
                        placeholder="Optional"
                      />
                    </label>
                  </div>
                  <div className="field span-4">
                    <label>
                      Maximum MRP
                      <input
                        value={mrpMax}
                        onChange={(e) => setMrpMax(e.target.value)}
                        placeholder="Optional"
                      />
                    </label>
                  </div>
                  <div className="field span-12">
                    <label>Current display text</label>
                    <div className="helper-text">
                      {selectedMrpItem.info_text ||
                        "No Std MRP / threshold configured"}
                    </div>
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={saveMrpSettings}
                  >
                    Save MRP settings
                  </button>
                </div>
              </>
            ) : (
              <div className="helper-text" style={{ marginTop: 12 }}>
                Search and select a {t("material").toLowerCase()} to configure
                its MRP threshold.
              </div>
            )}
          </div>
        </div>
      ) : null}
      <ModalShell
        open={!!unlinkedReplacementSubmit}
        title="Submit replacement with unmapped parameters?"
        subtitle="Some current specification rows are not linked to a source parameter from the withdrawn material chain."
        size="sm"
        onClose={() => setUnlinkedReplacementSubmit(null)}
        actions={(
          <>
            <button className="btn btn-secondary" type="button" onClick={() => setUnlinkedReplacementSubmit(null)}>
              Go back and link
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                const submitArgs = unlinkedReplacementSubmit;
                setUnlinkedReplacementSubmit(null);
                if (submitArgs) runMaterialSubmitAfterConfirm(submitArgs);
              }}
            >
              Submit anyway
            </button>
          </>
        )}
      >
        <div className="helper-text">
          Unmapped rows will be treated as new parameters. This can be intentional, but linked parameters help future charts and KPI continue across material versions.
        </div>
      </ModalShell>
      <MaterialTemplateModal state={templateModal.state} onClose={templateModal.closeTemplate} />
      <PinModal
        uid={user?.uid || ""}
        open={pinGate.pinOpen}
        onClose={pinGate.closePin}
        onVerified={pinGate.handlePinVerified}
      />
    </div>
  );
}
